import { useMemo } from "react";
import { displayValueWithUnit, getDisplayInfo } from "utils/formatting";
import { Header, Paragraph } from "components/atoms/Typography";
import { HandHoldingMoneyIcon, LeafIcon } from "components/atoms/Icon";
import ProjectCardSection from "./ProjectCardSection";

interface TotalCostAndSavingsCardProps {
    selectedView: "incremental" | "total";
    measure: CarbonReductionMeasure;
}

function TotalCostAndSavingsCard({ selectedView, measure }: TotalCostAndSavingsCardProps) {
    const {
        humanReadable: discountRateTitle,
        unit: discountRateUnit,
        formatFunction: discountFormatFn,
    } = getDisplayInfo("discountRate");

    const measureCostData = createMeasureCostPresentationData(measure, selectedView);
    return (
        <div className="large-card">
            <Header size="small">Costs & savings</Header>
            <ProjectCardSection
                key="netSavings"
                data={{
                    title: {
                        key: "netSavings",
                        humanReadableOverride: "Net savings",
                        icon: <LeafIcon />,
                        value: measure.netSavings,
                    },
                    body: [
                        {
                            key: "totalUtilityCostSavingsElectricity",
                            value: measure.lifetimeElectricityUtilityCostSavings,
                        },
                        {
                            key: "totalUtilityCostSavingsNaturalGas",
                            value: measure.lifetimeNaturalGasUtilityCostSavings,
                        },
                        {
                            key: "totalCarbonPolicyCostSavings",
                            value: measure.lifetimeCarbonCostsAvoided || 0.0,
                        },
                        {
                            key: "revenue",
                            value: measure.lifetimeRevenue,
                        },
                    ],
                }}
            />
            <ProjectCardSection key="totalMeasureCost" data={measureCostData} />
            <div className="discount-rate">
                <Paragraph size="small">{discountRateTitle}</Paragraph>
                <Paragraph size="small">
                    {displayValueWithUnit(measure.discountRate, discountRateUnit, (v) =>
                        discountFormatFn(Number(v) * 100)
                    )}
                </Paragraph>
            </div>
        </div>
    );
}

const createMeasureCostPresentationData = (measure: CarbonReductionMeasure, selectedView: "incremental" | "total") => {
    const body = [
        {
            key: "incrementalCost",
            value: measure.incrementalCost || 0,
        },
        {
            key: "totalIncentives",
            value: measure.totalIncentives,
        },
    ];

    if (selectedView === "total") {
        body.splice(1, 0, {
            key: "likeForLikeCost",
            value: measure.likeForLikeCost || 0,
        });
    }

    return {
        title: {
            key: "totalMeasureCost",
            humanReadableOverride: "Net cost",
            icon: <HandHoldingMoneyIcon />,
            value: selectedView === "total" ? measure.netTotalCost || 0 : measure.netIncrementalCost || 0,
        },
        body,
    };
};

export default TotalCostAndSavingsCard;
